import { Inspector, InspectorHost, ElementProposal } from '../../page-insights/inspector';
import { OverlayMode } from '../../overlays/overlay';
import { MobilePageRenderer } from '../renderer';
import {
  HumanReadableMobile,
  newSelectorPart,
  parseSelector,
  SelectorMatch,
  serializeSelector,
  serializeSelectorPart,
  SimilarSelectors,
} from '@fullstory/selectors';
import { MouseWatcher } from '../mouse_watcher';
import { DebugSocket, ViewMatch } from '@fullstory/playback-mobile';
import { ClickStats } from '../../page-insights/document_lookup';
import { ClickFacet, ClicksBySelector } from '../../page-insights/activitymaps';
import { SelectorChain } from '@fullstory/proto/fs/selectors';
import { ComputeElementStatsResponse } from '@fullstory/proto/fs/services/alexa';
import { WebInspector } from '../../web/page-insights/inspector';

const INSPECTOR_HOVER = '#438EE1';
const INSPECTOR_MATCH = '#F8598B';
const SCREEN_NAME_ATTR = 'screen-name';

// Note: these rules need to be kept in sync with addMobilePrivateByDefaultRules in services/ingestion-settings/evalblockrules.go
const rootSelectorsAndroid = ['decorview', 'root>decorview'].map(s => s.toLocaleLowerCase());
const rootSelectorsIos = ['UIApplication', 'UIWindow'].map(s => s.toLocaleLowerCase());
const rootSelectorsMobile = rootSelectorsAndroid.concat(rootSelectorsIos);

// Screen selectors that we don't ever want to suggest, because they are overly broad
const blockedScreenSelectors = ['swiftui', '[controller="UIViewController"]'].map(s =>
  s.toLowerCase(),
);

export class MobileInspector implements Inspector, OverlayMode {
  private _mouseWatcher: MouseWatcher;
  private _matchedCount: number;
  private _webViewInspectors: WebInspector[] = [];
  public hasSwiftUIMetaSelectorSupport: boolean;
  public hasSwiftUIStableSelectorsAvailable: boolean;
  public hasOptedIntoSwiftUIStableSelectors: boolean;
  public hasComposeStableSelectorsAvailable: boolean;
  public hasOptedIntoComposeStableSelectors: boolean;
  public missingFullStoryCommandLine: boolean;
  public isReactNative: boolean;

  constructor(
    private _renderer: MobilePageRenderer,
    private _host: InspectorHost,
    private _debugSocket: DebugSocket,
  ) {}

  start() {
    for (const oldWebViewInspector of this._webViewInspectors) {
      oldWebViewInspector?.stop();
    }

    this._webViewInspectors = [];

    for (const webView of this._renderer.getWebViews()) {
      if (webView) {
        webView?.inspector?.start();
        this._webViewInspectors.push(webView.inspector);
      }
    }

    const onViewClick = (match: ViewMatch) => {
      if (this._debugSocket) {
        const frame = this._renderer.getCurrentFrame();
        if (frame) {
          this._debugSocket.send(
            {
              message: 'click',
              view_id: match.view.uniqueId,
              frame_index: frame.frame.frameIndex(),
            },
            frame.bundle.bb.bytes(),
          );
        }
      }

      const longMatch = match.selector();
      const longMatchStr = serializeSelector(longMatch);

      // Note that the fully specified selector might match more than one element sometimes. That's just the
      // nature of how we generate these selectors (we can't use something N-th child here because that ain't what we
      // do in other parts of the system. And it might also not be what we want anyways due to over-specificity).
      this.select(longMatchStr, longMatch);
    };

    const onViewHover = (match: ViewMatch) => {
      const r = this._renderer.canvasRenderer();

      if (match && match.view.isWebView) {
        r.disablePointerEvents();
        return;
      } else {
        r.enablePointerEvents();
      }

      r.rerender();
      if (match) {
        r.highlightSingle(match, INSPECTOR_HOVER);
      }
    };

    this._mouseWatcher = new MouseWatcher(
      this._renderer.view(),
      this._renderer.canvasRenderer(),
      onViewHover,
      onViewClick,
    );
    this._mouseWatcher.start();
  }

  stop() {
    this._mouseWatcher.stopAndDispose();

    for (const webViewInspector of this._webViewInspectors) {
      webViewInspector?.stop();
    }

    this._webViewInspectors = [];

    // also clear any highlights on stop as they are no longer relevant
    const r = this._renderer.canvasRenderer();
    r.rerender(false);
  }

  preview(selector: string) {
    const r = this._renderer.canvasRenderer();
    let sel: SelectorChain | undefined;
    r.rerender();
    try {
      sel = parseSelector(selector);
    } catch (e) {
      // TODO: ideally this parsing should happen in the UI
      console.error(`Failed to parse selector '${selector}'`, e);
      return;
    }
    this._matchedCount = r.highlight(sel, INSPECTOR_MATCH);
  }

  select(selectorStr: string, selectorArg?: SelectorChain): void {
    let selector = selectorArg;
    const notTyping = !!selector;
    if (!selector) {
      selector = parseSelector(selectorStr);
    }
    if (selectorStr.includes('SwiftUI') && !this.hasOptedIntoSwiftUIStableSelectors) {
      // Clear any rendered highlights.
      this._renderer.canvasRenderer().rerender(false);
      if (this.hasSwiftUIStableSelectorsAvailable) {
        /* This message can also happen to users with new enough versions of
         * the plugin, even if they are not using the SwiftUI version.
         */
        this._host.complainSelector(
          'SwiftUI selector stability has not been enabled for this app.  To add privacy rules excluding all SwiftUI elements in the app, use the selector "SwiftUI".',
          'https://help.fullstory.com/hc/en-us/articles/8867138701719-Integrating-FullStory-into-a-SwiftUI-App',
        );
      } else if (this.missingFullStoryCommandLine) {
        this._host.complainSelector(
          'SwiftUI selector stability support could not be determined. Was FullStoryCommandLine run successfully for this app build?',
          'https://help.fullstory.com/hc/en-us/articles/360042772333-Getting-Started-with-iOS-Data-Capture#01FP0FKAH2WAPFHK0JQK9CVCFT',
        );
      } else if (this.hasSwiftUIMetaSelectorSupport) {
        this._host.complainSelector(
          'SwiftUI selectors are not supported.  To add privacy rules excluding all SwiftUI elements in the app, use the selector "SwiftUI".',
          'https://help.fullstory.com/hc/en-us/articles/8867138701719-Integrating-FullStory-into-a-SwiftUI-App',
        );
      } else {
        this._host.complainSelector(
          'SwiftUI selectors are not supported.  To add privacy rules excluding all SwiftUI elements in the app, you must upgrade to version 1.20.0 or higher of the FullStory plugin.',
          'https://help.fullstory.com/hc/en-us/articles/8867138701719-Integrating-FullStory-into-a-SwiftUI-App',
        );
      }
      return;
    }
    // Test if this contains `androidcomposeview`, but does not end with `androidcomposeview`
    if (
      selectorStr.includes('androidcomposeview') &&
      !selectorStr.endsWith('androidcomposeview') &&
      !this.hasOptedIntoComposeStableSelectors
    ) {
      // Clear any rendered highlights.
      this._renderer.canvasRenderer().rerender(false);
      this._host.complainSelector(
        'Jetpack Compose selectors are not supported.  To add privacy rules excluding all Jetpack Compose elements in the app, use the selector "androidcomposeview".',
      );
      return;
    }
    const screenSelector = this.screenSelector();
    // Warn for unsupported mobile unmask-at-root selectors
    if (this.isRootSelector(selectorStr)) {
      // if they were clicking on the app (as opposed to typing in a selector), try to suggest a screen selector instead
      if (notTyping && screenSelector) {
        return this.select(screenSelector);
      }
      this._host.complainSelector('Root-level unmasking of mobile apps is not permitted.');
      return;
    }

    this.preview(selectorStr);
    if (selectorStr) {
      const proposal: ElementProposal = {
        fullSelector: <SelectorMatch>{
          Selector: selectorStr,
          HumanReadableSelector: serializeSelector(
            HumanReadableMobile(selector, this.isReactNative),
          ),
          Count: this._matchedCount,
          Mobile: true,
        },
        similarElements: SimilarSelectors(selector),
      };
      this._host.updateProposal(proposal, notTyping);
    }
  }

  generateStats(rsp: ComputeElementStatsResponse, selector: string) {
    const facet = ClickFacet.fromResponse(rsp);
    if (facet.totalClicks < 1) {
      console.log(`didn't get any click stats`);
      this._host.showStats(facet, [], this._renderer.getNativePlatform());
      return;
    }
    const clickStats = new ClickStats(
      facet,
      'user',
      new ClicksBySelector(
        parseSelector(selector),
        selector,
        selector,
        '',
        undefined,
        facet.totalClicks,
        facet.totalUsers,
      ),
      true,
    );
    this._host.showStats(facet, clickStats.stats().Stats, this._renderer.getNativePlatform());
  }

  isRootSelector(selectorStr: string): boolean {
    return rootSelectorsMobile.includes(selectorStr.toLowerCase());
  }

  getLogicalPageContentRoot() {
    const screenSelector = this.screenSelector();
    this._host.updateLogicalPageContentRoot({ Selector: screenSelector, Mobile: true });
  }

  screenSelector(): string | null {
    const frame = this._renderer.getCurrentFrame();
    if (frame.screenRootViewId64 == '0') {
      return null;
    }
    let selector = frame.findViewById(frame.screenRootViewId64)?.getSelectorRule();

    let matchedKey: string;
    let matchedValue: string;

    if (this.isReactNative) {
      // Trim the selector down to only the "screen-name" attribute
      if (selector.attrs[SCREEN_NAME_ATTR]) {
        matchedKey = SCREEN_NAME_ATTR;
        matchedValue = selector.attrs[SCREEN_NAME_ATTR];
      } else {
        // For RN, assume that if "screen-name" attribute doesn't exist, the resulting screen selector will be generic.
        // As a result, we avoid returning a generic selector here.
        return null;
      }
    } else {
      // for iOS, if we have a controller attribute, then trim the selector down to only the controller
      // for Android, the equivalent attribute is "fragment"
      for (const attr of ['controller', 'fragment']) {
        if (selector?.attrs[attr]) {
          matchedKey = attr;
          matchedValue = selector.attrs[attr];
          break;
        }
      }
    }
    if (matchedKey) {
      selector = newSelectorPart();
      selector.attrs[matchedKey] = matchedValue;
      selector.attrMatch[matchedKey] = '=';
    }
    const selectorString = (selector && serializeSelectorPart(selector)) || null;
    if (
      selectorString &&
      // filter out a couple of things that may be true, but are not helpful
      !this.isRootSelector(selectorString) &&
      !blockedScreenSelectors.includes(selectorString.toLowerCase())
    ) {
      return selectorString;
    }
    return null;
  }
}
